import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

import { funnelsKey, getFunnels, messageFor, type FunnelSummary } from "./api";
import { useControls, WindowControl } from "./controls";
import { countingStartedInWindow, formatCount, formatDay, formatPct, needsAttention, signupsInWindow } from "./format";

const numeric = { meta: { align: "right" as const } };

const COLUMNS: ColumnDef<FunnelSummary, unknown>[] = [
  { id: "page", header: "Page", accessorFn: (row) => row.page_path },
  { id: "visitors", header: "Visitors", accessorFn: (row) => formatCount(row.new_visitors), ...numeric },
  { id: "signups", header: "Sign-ups", accessorFn: (row) => formatCount(signupsInWindow(row)), ...numeric },
  {
    id: "conversion",
    // Sign-ups count the whole window; the rate only divides days that have
    // visitor numbers, so the header says which span it is over.
    header: "Conversion (since counting began)",
    accessorFn: (row) => formatPct(row.conversion_pct),
    ...numeric,
  },
  { id: "drip", header: "Reached Drip", accessorFn: (row) => formatCount(row.reached_drip), ...numeric },
  {
    id: "attention",
    header: "Needs attention",
    // Blank when nothing is stuck: a column of zeros reads as a warning.
    accessorFn: (row) => (needsAttention(row) > 0 ? formatCount(needsAttention(row)) : ""),
    ...numeric,
  },
];

/**
 * Every landing-page funnel on one row each. The table holds at most a handful
 * of funnels, so the whole list is one page.
 */
export function OverviewTab({ areaName }: { areaName: string }) {
  const controls = useControls();
  const funnels = useQuery({
    queryKey: funnelsKey(controls.days),
    queryFn: () => getFunnels(controls.days),
  });

  if (funnels.isError) {
    return (
      <Alert>
        <AlertTitle>Landing page figures unavailable</AlertTitle>
        <AlertDescription>{messageFor(funnels.error, areaName)}</AlertDescription>
      </Alert>
    );
  }

  const rows = funnels.data?.data.funnels ?? [];
  const countingStarted = countingStartedInWindow(rows, controls.days);

  return (
    <div className="flex flex-col gap-4">
      <WindowControl controls={controls} />
      <DataTable
        columns={COLUMNS}
        rows={rows}
        pending={funnels.isPending}
        page={{ pageIndex: 0, pageSize: Math.max(rows.length, 1), rowCount: rows.length }}
        onPageChange={() => {}}
        emptyMessage="No landing page has recorded a visit or a sign-up yet."
        caption={`Landing page funnels, last ${controls.days} days`}
      />
      {countingStarted && (
        <p className="text-sm text-muted-foreground">
          Visitor counting started on {formatDay(countingStarted)}; earlier days have sign-ups but no visitor
          numbers.
        </p>
      )}
    </div>
  );
}
